import { useState, useEffect, memo, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRightIcon, PlayIcon } from "@heroicons/react/24/outline";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation, EffectFade } from "swiper/modules";
import TypedText from "./TypedText";
import YouTubeModal from "@/components/common/YouTubeModal";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";
import "swiper/css/navigation";

// Mobile-optimized Image Component
const MobileImage = memo(({ src, alt, isActive }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => setIsLoaded(true);
    img.onerror = () => setIsLoaded(true);
  }, [src]);

  return (
    <>
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-800" />
      )}
      <img
        src={src}
        alt={alt}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
          isLoaded && isActive ? 'opacity-100' : 'opacity-0'
        }`}
        loading="lazy"
        decoding="async"
        style={{ willChange: 'opacity' }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/30 to-transparent" />
    </>
  );
});

MobileImage.displayName = "MobileImage";

// Desktop Image Component
const DesktopImage = memo(({ src, alt, isActive }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => setIsLoaded(true);
    img.onerror = () => setIsLoaded(true);
  }, [src]);

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden">
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 animate-pulse" />
      )}

      {isLoaded && (
        <motion.img
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.08, opacity: 0, filter: "blur(20px)" }}
          animate={
            isActive
              ? { scale: 1, opacity: 1, filter: "blur(0px)" }
              : { scale: 1.15, opacity: 0.6, filter: "blur(12px)" }
          }
          transition={{ duration: 2, ease: "easeOut" }}
        />
      )}
    </div>
  );
});

DesktopImage.displayName = "DesktopImage";

// Mobile Scroll Indicator
const MobileScrollIndicator = memo(({ lng }) => (
  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20">
    <div className="flex flex-col items-center gap-1">
      <div className="w-5 h-8 border border-white/20 rounded-full flex justify-center">
        <div className="w-1 h-3 bg-white/60 rounded-full mt-1 animate-bounce" />
      </div>
      <span className="text-xs text-white/70 font-medium">
        {lng === "ar" ? "اسحب" : lng === "fr" ? "Glissez" : "Swipe"}
      </span>
    </div>
  </div>
));

MobileScrollIndicator.displayName = "MobileScrollIndicator";

// Desktop Scroll Indicator
const DesktopScrollIndicator = memo(({ lng }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 1.5, duration: 0.8 }}
    className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 pointer-events-none"
  >
    <div className="flex flex-col items-center gap-2">
      <span className="text-sm text-white/80 font-medium mb-2 text-shadow-sm">
        {lng === "ar" ? "قم بالتمرير" : lng === "fr" ? "Faites défiler" : "Scroll Down"}
      </span>
      <motion.div
        className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center backdrop-blur-sm"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.5 }}
      >
        <motion.div
          className="w-1.5 h-3 bg-gradient-to-b from-white to-white/60 rounded-full mt-2"
          animate={{ opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.3 }}
        />
      </motion.div>
    </div>
  </motion.div>
));

DesktopScrollIndicator.displayName = "DesktopScrollIndicator";

// Main Component
const HeroSection = ({ seedData, lng }) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const swiperRef = useRef(null);

  const getText = useCallback(
    (obj, key) => (obj ? obj[`${key}_${lng}`] || obj[key] || obj[`${key}_en`] || "" : ""),
    [lng]
  );

  // Mobile detection with debounce
  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 768);
      }, 100);
    };
    
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const handleSlideChange = useCallback((swiper) => {
    setActiveSlideIndex(swiper.realIndex || swiper.activeIndex);
  }, []);

  const handleSwiperInit = useCallback((swiper) => {
    swiperRef.current = swiper;
    setActiveSlideIndex(swiper.realIndex || swiper.activeIndex);
  }, []);

  const heroSlides = useCallback(() => [
    {
      image: "/assets/images/NSPObuilding03.webp",
      title: getText(seedData?.hero, "title"),
      subtitle: getText(seedData?.hero, "subtitle"),
      cta: getText(seedData?.hero, "cta"),
    },
    {
      image: "/assets/images/hero/4.webp",
      title: lng === "ar"
        ? "مشروعات قومية عملاقة"
        : lng === "fr"
        ? "Grands projets nationaux"
        : "Mega National Projects",
      subtitle: lng === "ar"
        ? "استثمار في المستقبل وبنية تحتية متطورة"
        : lng === "fr"
        ? "Investir dans l'avenir et des infrastructures modernes"
        : "Investing in the future with modern infrastructure",
      cta: lng === "ar"
        ? "صور من مشاريعنا"
        : lng === "fr"
        ? "Découvrir les projets"
        : "Explore Projects",
    },
    {
      image: "/assets/images/hero/owinat8.webp",
      title: lng === "ar"
        ? "ابتكار ونمو مستدام"
        : lng === "fr"
        ? "Innovation et croissance durable"
        : "Innovation and Sustainable Growth",
      subtitle: lng === "ar"
        ? "حلول ذكية تدعم الاقتصاد الوطني"
        : lng === "fr"
        ? "Des solutions intelligentes pour l'économie nationale"
        : "Smart solutions powering the economy",
      cta: lng === "ar"
        ? "تعرف علينا"
        : lng === "fr"
        ? "À propos de nous"
        : "About Us",
    },
    {
      image: "/assets/images/hero/2.webp",
      title: lng === "ar"
        ? "استثمار مدروس… لمستقبل لا يعرف الحدود"
        : lng === "fr"
        ? "Un investissement réfléchi… pour un avenir sans limites"
        : "A well-planned investment… for a future without limits",
      subtitle: lng === "ar"
        ? "رؤية استراتيجية تعزز النجاح وتفتح آفاقًا جديدة"
        : lng === "fr"
        ? "Une vision stratégique qui renforce la réussite et ouvre de nouveaux horizons"
        : "A strategic vision that drives success and opens new horizons",
      cta: lng === "ar"
        ? "اكتشف المزيد"
        : lng === "fr"
        ? "Découvrir plus"
        : "Discover More",
    },
  ], [seedData, lng, getText]);

  // Mobile-optimized Swiper settings
  const mobileSwiperSettings = {
    modules: [Autoplay, Pagination],
    effect: "slide",
    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
    },
    pagination: {
      clickable: true,
      bulletClass: "swiper-pagination-bullet !w-2 !h-2 !bg-white/60",
      bulletActiveClass: "!bg-white !w-6",
    },
    loop: true,
    speed: 300,
    resistanceRatio: 0.5,
    touchRatio: 0.5,
    shortSwipes: true,
    followFinger: true,
    freeMode: false,
    watchSlidesProgress: false,
    preventClicks: false,
    preventClicksPropagation: false,
    allowTouchMove: true,
  };

  // Desktop Swiper settings
  const desktopSwiperSettings = {
    modules: [Autoplay, Pagination, Navigation, EffectFade],
    effect: "fade",
    fadeEffect: { crossFade: true },
    autoplay: {
      delay: 10000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    },
    pagination: {
      clickable: true,
      bulletClass: "swiper-pagination-bullet !w-3 !h-3 !rounded-full bg-white/40 hover:bg-white/60 transition-all duration-300",
      bulletActiveClass: "!bg-white !w-8 !h-1.5 !rounded-full",
    },
    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev",
    },
    loop: true,
    speed: 1000,
  };

  return (
    <section className="relative min-h-screen w-full bg-slate-900 text-white overflow-hidden">
      {/* Mobile-optimized version */}
      {isMobile ? (
        <>
          <Swiper
            {...mobileSwiperSettings}
            onSlideChange={handleSlideChange}
            onSwiper={handleSwiperInit}
            className="w-full h-screen"
          >
            {heroSlides().map((slide, idx) => (
              <SwiperSlide key={idx}>
                <div className="relative w-full h-screen">
                  <MobileImage 
                    src={slide.image} 
                    alt="hero" 
                    isActive={activeSlideIndex === idx} 
                  />
                  
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-4 text-center">
                    <div className="max-w-md">
                      {/* Title */}
                      <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight drop-shadow-lg">
                        {slide.title}
                      </h1>
                      
                      {/* Subtitle */}
                      <p className="text-base sm:text-lg text-white/90 mb-5 leading-relaxed">
                        {slide.subtitle}
                      </p>
                      
                      {/* Buttons */}
                      <div className="flex flex-col gap-3">
                        <Link to="/gallery">
                          <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg active:scale-95 transition-all duration-200 shadow-md">
                            <span className="flex items-center justify-center gap-2">
                              {slide.cta}
                              <ArrowRightIcon className="h-4 w-4 rtl:rotate-180" />
                            </span>
                          </button>
                        </Link>
                        
                        <button
                          onClick={() => setIsVideoModalOpen(true)}
                          className="w-full py-3 px-4 rounded-lg border border-white/30 text-white font-semibold hover:bg-white/10 active:scale-95 transition-all duration-200"
                        >
                          <span className="flex items-center justify-center gap-2">
                            <PlayIcon className="h-4 w-4" />
                            {lng === "ar"
                              ? "مشاهدة الفيديو"
                              : lng === "fr"
                              ? "Regarder la vidéo"
                              : "Watch Video"}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          
          <MobileScrollIndicator lng={lng} />
        </>
      ) : (
        <>
          {/* Desktop version */}
          <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px] z-0" />
          
          <Swiper
            {...desktopSwiperSettings}
            onSlideChange={handleSlideChange}
            onSwiper={handleSwiperInit}
            className="w-full h-screen !overflow-visible"
          >
            {heroSlides().map((slide, idx) => (
              <SwiperSlide key={idx} className="!overflow-visible">
                <div className="relative w-full h-screen">
                  <DesktopImage 
                    src={slide.image} 
                    alt="hero" 
                    isActive={activeSlideIndex === idx} 
                  />
                  
                  <div className="relative z-10 h-full flex items-center justify-center">
                    <div className="max-w-6xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white text-shadow-lg mb-6">
                        <TypedText 
                          text={slide.title} 
                          isActive={activeSlideIndex === idx} 
                          speed={140}
                          cursorColor="#fbbf24"
                        />
                      </h1>
                      
                      <p className="text-lg sm:text-xl lg:text-2xl font-bold text-white/95 mb-8">
                        <TypedText 
                          text={slide.subtitle} 
                          isActive={activeSlideIndex === idx}
                          delay={300}
                          speed={120}
                          cursorColor="#60a5fa"
                          startDelay={1000}
                        />
                      </p>
                      
                      <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6">
                        <Link to="/gallery" className="w-full sm:w-auto">
                          <button className="bg-gradient-to-r from-blue-600/95 to-cyan-500/95 hover:from-blue-700 hover:to-cyan-600 text-white font-medium px-10 py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 w-full sm:w-auto">
                            <span className="flex items-center gap-2">
                              {slide.cta}
                              <ArrowRightIcon className="h-5 w-5 rtl:rotate-180" />
                            </span>
                          </button>
                        </Link>
                        
                        <button
                          onClick={() => setIsVideoModalOpen(true)}
                          className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border border-white/30 text-white/90 hover:bg-white/10 hover:text-white transition-all duration-300 w-full sm:w-auto"
                        >
                          <PlayIcon className="h-5 w-5" />
                          <span className="font-medium">
                            {lng === "ar"
                              ? "مشاهدة الفيديو"
                              : lng === "fr"
                              ? "Regarder la vidéo"
                              : "Watch Video"}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          
          <DesktopScrollIndicator lng={lng} />
          
          {/* Navigation buttons for desktop */}
          <div className="swiper-button-prev absolute left-6 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/30 hover:bg-white/20 flex items-center justify-center text-white cursor-pointer transition-all duration-300">
            <ArrowRightIcon className="h-5 w-5 rotate-180" />
          </div>
          <div className="swiper-button-next absolute right-6 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/30 hover:bg-white/20 flex items-center justify-center text-white cursor-pointer transition-all duration-300">
            <ArrowRightIcon className="h-5 w-5" />
          </div>
        </>
      )}

      {/* YouTube Modal */}
      <YouTubeModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="FmGmtWvQmOg"
      />

      {/* Performance styles */}
      <style>{`
        @media (max-width: 767px) {
          * {
            -webkit-tap-highlight-color: transparent;
          }
          .swiper-pagination {
            bottom: 20px !important;
          }
        }
        .text-shadow-lg { 
          text-shadow: 0 4px 20px rgba(0,0,0,0.5); 
        }
        .text-shadow-sm { 
          text-shadow: 0 2px 10px rgba(0,0,0,0.3); 
        }
        .animate-bounce {
          animation: bounce 1.5s infinite;
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
      `}</style>
    </section>
  );
};

HeroSection.displayName = "HeroSection";

export default memo(HeroSection);